import { useEffect, useMemo, useState } from "react";
import { Rnd } from "react-rnd";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Eye, EyeOff, Printer } from "lucide-react";

const PAPER_PRESETS = {
  "A4-portrait":   { width_mm: 210, height_mm: 297, label: "A4 portrait (210×297mm)" },
  "A4-landscape":  { width_mm: 297, height_mm: 210, label: "A4 landscape" },
  "A5-portrait":   { width_mm: 148, height_mm: 210, label: "A5 portrait (148×210mm)" },
  "A5-landscape":  { width_mm: 210, height_mm: 148, label: "A5 landscape" },
  "letter":        { width_mm: 216, height_mm: 279, label: "US Letter" },
};

// Local-only fallback cache so the dropdown survives a page-refresh blip before /label-layout
// resolves. Once layout loads, DB-stored custom_presets become the source of truth.
const PRESET_KEY = "eaassy_label_custom_presets";
function loadCustomPresets() {
  try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "{}"); } catch { return {}; }
}
function saveCustomPresets(p) {
  try { localStorage.setItem(PRESET_KEY, JSON.stringify(p)); } catch { /* quota */ }
}

const FIELD_DESCRIPTIONS = {
  brand:   "Brand / shop name (top accent)",
  name:    "Product name (multi-line)",
  barcode: "Code-128 barcode image",
  mrp:     "MRP / price (bottom-left)",
  phone:   "Contact phone (bottom-right)",
};

const SAMPLE = {
  brand:   "My Outlet",
  name:    "Aaria Silk Suit · Pink",
  barcode: "TSS11885",
  mrp:     "MRP ₹2,499",
  phone:   "+91 98xxx 12345",
};

// Render canvas resolution multiplier — 4 px per mm gives a crisp preview without huge DOM dims.
const PX_PER_MM = 4;

export default function LabelBuilder() {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [customPresets, setCustomPresets] = useState(loadCustomPresets());
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customW, setCustomW] = useState(105);
  const [customH, setCustomH] = useState(148);

  const allPresets = useMemo(() => ({ ...PAPER_PRESETS, ...customPresets }), [customPresets]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/organisation/label-layout");
        setLayout(r.data);
        // Hydrate dropdown with custom presets stored on the layout document (DB-backed).
        // Merge with local cache so old browsers don't lose their list while migrating.
        const fromDb = (r.data && r.data.custom_presets) || {};
        if (Object.keys(fromDb).length) {
          const merged = { ...loadCustomPresets(), ...fromDb };
          setCustomPresets(merged);
          saveCustomPresets(merged);
        }
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pageDims = useMemo(() => {
    if (!layout) return PAPER_PRESETS["A4-portrait"];
    const preset = layout.page?.preset || "A4-portrait";
    const preset_dims = allPresets[preset] || PAPER_PRESETS["A4-portrait"];
    return {
      ...preset_dims,
      width_mm: layout.page?.width_mm || preset_dims.width_mm,
      height_mm: layout.page?.height_mm || preset_dims.height_mm,
    };
  }, [layout, allPresets]);

  const saveCustomPreset = () => {
    const w_mm = parseInt(customW);
    const h_mm = parseInt(customH);
    const name = customName.trim();
    if (!name || !w_mm || !h_mm) {
      toast.error("Name, width and height required");
      return;
    }
    const key = `custom-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    const dims = { width_mm: w_mm, height_mm: h_mm, label: `${name} (${w_mm}×${h_mm}mm)`, custom: true };
    const next = { ...customPresets, [key]: dims };
    setCustomPresets(next);
    saveCustomPresets(next);
    // Persist on the layout document so it survives across browsers / devices.
    setLayout({
      ...layout,
      page: { ...layout.page, ...dims, preset: key },
      custom_presets: next,
    });
    setShowCustomForm(false);
    setCustomName("");
    toast.success(`Custom preset "${name}" saved · click Save Layout to sync to all browsers`);
  };

  const deleteCustomPreset = (key) => {
    const { [key]: _drop, ...rest } = customPresets;
    setCustomPresets(rest);
    saveCustomPresets(rest);
    const isCurrent = layout.page?.preset === key;
    setLayout({
      ...layout,
      page: isCurrent ? { ...PAPER_PRESETS["A4-portrait"], preset: "A4-portrait" } : layout.page,
      custom_presets: rest,
    });
    toast.success("Custom preset removed · click Save Layout to sync");
  };

  const labelDimsMm = useMemo(() => {
    if (!layout) return { w: 64, h: 27 };
    const cols = layout.grid?.cols || 3;
    const rows = layout.grid?.rows || 10;
    const margin = layout.grid?.margin_mm ?? 8;
    return {
      w: (pageDims.width_mm - 2 * margin) / cols,
      h: (pageDims.height_mm - 2 * margin) / rows,
    };
  }, [layout, pageDims]);

  // Canvas pixel dims for one label cell
  const cellPx = useMemo(() => ({
    w: labelDimsMm.w * PX_PER_MM,
    h: labelDimsMm.h * PX_PER_MM,
  }), [labelDimsMm]);

  const updateField = (key, patch) => {
    setLayout((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)),
    }));
  };

  const updateGrid = (patch) => {
    setLayout((prev) => ({ ...prev, grid: { ...prev.grid, ...patch } }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Always include the current custom_presets so the DB stays canonical across browsers.
      const payload = { ...layout, custom_presets: customPresets };
      await api.put("/organisation/label-layout", payload);
      toast.success("Label layout saved. Will apply on every browser & device.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!confirm("Reset to the default label layout?")) return;
    try {
      const r = await api.post("/organisation/label-layout/reset");
      setLayout(r.data);
      toast.success("Reset to default layout.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const onTestPrint = async () => {
    // Save first then trigger a test barcode-labels render for the first product available
    try {
      await api.put("/organisation/label-layout", { ...layout, custom_presets: customPresets });
      const r = await api.get("/products?page=1&page_size=1");
      const first = (r.data?.rows || r.data || [])[0];
      if (!first) {
        toast.error("No products available to test print. Create a product first.");
        return;
      }
      const pdf = await api.post(
        "/barcode-labels",
        { items: [{ product_id: first.id, count: 6 }] },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(pdf.data);
      window.open(url, "_blank");
      toast.success("Test labels generated — opening PDF preview…");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test print failed");
    }
  };

  if (loading || !layout) {
    return <div className="p-8 text-slate-500" data-testid="label-builder-loading">Loading label template…</div>;
  }

  const selectedField = selected ? layout.fields.find((f) => f.key === selected) : null;

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-slate-100" data-testid="label-builder-page">
      {/* Left palette */}
      <aside className="w-72 border-r border-slate-200 bg-white p-4 overflow-y-auto" data-testid="label-builder-palette">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Page & Grid</h2>
        <div className="space-y-3 mb-6">
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Paper preset</label>
            <select
              value={layout.page?.preset || "A4-portrait"}
              onChange={(e) => {
                const dims = allPresets[e.target.value];
                setLayout({ ...layout, page: { ...layout.page, ...dims, preset: e.target.value } });
              }}
              className="h-9 border border-slate-300 px-2 text-sm w-full"
              data-testid="label-paper-select"
            >
              {Object.entries(allPresets).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setShowCustomForm((s) => !s)} className="text-[11px] underline text-slate-500" data-testid="label-custom-toggle">
                + Custom size
              </button>
              {layout.page?.preset?.startsWith("custom-") && (
                <button onClick={() => deleteCustomPreset(layout.page.preset)} className="text-[11px] underline text-rose-600" data-testid="label-custom-delete">
                  Delete preset
                </button>
              )}
            </div>
            {showCustomForm && (
              <div className="mt-2 p-2 border border-slate-200 bg-slate-50 space-y-2" data-testid="label-custom-form">
                <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Preset name" className="h-7 px-2 border border-slate-300 text-xs w-full" data-testid="label-custom-name" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block">W (mm)</label>
                    <input type="number" value={customW} onChange={(e) => setCustomW(e.target.value)} className="h-7 px-2 border border-slate-300 text-xs w-full" data-testid="label-custom-w" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block">H (mm)</label>
                    <input type="number" value={customH} onChange={(e) => setCustomH(e.target.value)} className="h-7 px-2 border border-slate-300 text-xs w-full" data-testid="label-custom-h" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCustomPreset} className="flex-1 h-7 bg-slate-900 text-white text-xs hover:bg-slate-800" data-testid="label-custom-save">Save</button>
                  <button onClick={() => setShowCustomForm(false)} className="flex-1 h-7 border border-slate-300 text-xs hover:bg-slate-100">Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <NumberField label="Cols" value={layout.grid?.cols || 3} onChange={(v) => updateGrid({ cols: v })} testid="grid-cols" />
            <NumberField label="Rows" value={layout.grid?.rows || 10} onChange={(v) => updateGrid({ rows: v })} testid="grid-rows" />
            <NumberField label="Margin mm" value={layout.grid?.margin_mm ?? 8} onChange={(v) => updateGrid({ margin_mm: v })} testid="grid-margin" />
          </div>
          <div className="text-[11px] text-slate-500">
            Label cell: <b>{labelDimsMm.w.toFixed(1)}mm × {labelDimsMm.h.toFixed(1)}mm</b> ({(layout.grid?.cols || 3) * (layout.grid?.rows || 10)} per page)
          </div>
        </div>

        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Fields</h2>
        <ul className="space-y-1">
          {layout.fields.map((f) => (
            <li key={f.key}>
              <button
                onClick={() => setSelected(f.key)}
                className={`w-full text-left px-3 py-2 text-sm border ${
                  selected === f.key ? "border-slate-900 bg-slate-50 font-semibold" : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`label-field-${f.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span>{f.label}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); updateField(f.key, { visible: !f.visible }); }}
                    data-testid={`label-toggle-${f.key}`}
                  >
                    {f.visible ? <Eye size={14} /> : <EyeOff size={14} className="text-slate-400" />}
                  </button>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{FIELD_DESCRIPTIONS[f.key]}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Canvas */}
      <main className="flex-1 overflow-auto p-8">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold tracking-tight">Single label preview</h2>
          <div className="ml-auto flex gap-2">
            <button onClick={onTestPrint} className="h-9 px-3 border border-slate-300 text-sm hover:bg-slate-100 inline-flex items-center gap-1" data-testid="label-test-print">
              <Printer size={14} /> Test Print
            </button>
            <button onClick={onReset} className="h-9 px-3 border border-slate-300 text-sm hover:bg-slate-100 inline-flex items-center gap-1" data-testid="label-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
            <button onClick={onSave} disabled={saving} className="h-9 px-4 bg-slate-900 text-white text-sm hover:bg-slate-800 inline-flex items-center gap-1 disabled:opacity-50" data-testid="label-save-btn">
              <Save size={14} /> {saving ? "Saving…" : "Save Layout"}
            </button>
          </div>
        </div>

        <div
          className="relative bg-white shadow-md border-2 border-slate-300 mx-auto"
          style={{ width: cellPx.w * 4, height: cellPx.h * 4 }}
          onClick={() => setSelected(null)}
          data-testid="label-canvas"
        >
          <div
            style={{
              position: "absolute",
              top: 0, left: 0,
              width: cellPx.w,
              height: cellPx.h,
              transform: "scale(4)",
              transformOrigin: "top left",
            }}
          >
            {layout.fields.filter((f) => f.visible).map((f) => {
              const x = (f.x_pct / 100) * cellPx.w;
              const y = (f.y_pct / 100) * cellPx.h;
              const w = (f.w_pct / 100) * cellPx.w;
              const h = (f.h_pct / 100) * cellPx.h;
              return (
                <Rnd
                  key={f.key}
                  position={{ x, y }}
                  size={{ width: w, height: h }}
                  onDragStop={(_e, d) => updateField(f.key, {
                    x_pct: Math.max(0, Math.min(100 - f.w_pct, (d.x / cellPx.w) * 100)),
                    y_pct: Math.max(0, Math.min(100 - f.h_pct, (d.y / cellPx.h) * 100)),
                  })}
                  onResizeStop={(_e, _dir, ref, _delta, position) =>
                    updateField(f.key, {
                      w_pct: (parseInt(ref.style.width) / cellPx.w) * 100,
                      h_pct: (parseInt(ref.style.height) / cellPx.h) * 100,
                      x_pct: (position.x / cellPx.w) * 100,
                      y_pct: (position.y / cellPx.h) * 100,
                    })
                  }
                  bounds="parent"
                  onClick={(e) => { e.stopPropagation(); setSelected(f.key); }}
                  className={`flex items-center px-1 cursor-move ${
                    selected === f.key ? "outline outline-2 outline-blue-600 bg-blue-50/60" : "outline outline-1 outline-dashed outline-slate-400 hover:outline-slate-600"
                  }`}
                  data-testid={`label-rnd-${f.key}`}
                >
                  <FieldPreview field={f} />
                </Rnd>
              );
            })}
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-3 text-center">
          Preview at 4× zoom · drag-resize fields within the label cell · positions saved as percent so they scale across paper sizes
        </div>
      </main>

      {/* Right properties */}
      <aside className="w-72 border-l border-slate-200 bg-white p-4 overflow-y-auto" data-testid="label-properties">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Properties</h2>
        {!selectedField ? (
          <p className="text-sm text-slate-500">Select a field on the canvas to edit its position, size, and font.</p>
        ) : (
          <div className="space-y-3" data-testid="label-props-form">
            <div>
              <div className="text-xs font-medium text-slate-700">{selectedField.label}</div>
              <div className="text-[11px] text-slate-500">{FIELD_DESCRIPTIONS[selectedField.key]}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="X %" value={Math.round(selectedField.x_pct)} onChange={(v) => updateField(selectedField.key, { x_pct: v })} testid="lprop-x" />
              <NumberField label="Y %" value={Math.round(selectedField.y_pct)} onChange={(v) => updateField(selectedField.key, { y_pct: v })} testid="lprop-y" />
              <NumberField label="Width %" value={Math.round(selectedField.w_pct)} onChange={(v) => updateField(selectedField.key, { w_pct: v })} testid="lprop-w" />
              <NumberField label="Height %" value={Math.round(selectedField.h_pct)} onChange={(v) => updateField(selectedField.key, { h_pct: v })} testid="lprop-h" />
              <NumberField label="Font size" value={selectedField.font_size} onChange={(v) => updateField(selectedField.key, { font_size: v })} testid="lprop-fontsize" />
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Align</label>
                <select
                  value={selectedField.align}
                  onChange={(e) => updateField(selectedField.key, { align: e.target.value })}
                  className="h-8 border border-slate-300 px-2 text-xs w-full"
                  data-testid="lprop-align"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={selectedField.bold}
                onChange={(e) => updateField(selectedField.key, { bold: e.target.checked })}
                data-testid="lprop-bold"
              />
              Bold
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={selectedField.visible}
                onChange={(e) => updateField(selectedField.key, { visible: e.target.checked })}
                data-testid="lprop-visible"
              />
              Visible
            </label>
            {selectedField.key === "barcode" && (
              <label className="flex items-center gap-2 text-xs" title="Tiny labels can drop the digits and use the full cell height for taller, more scan-friendly bars">
                <input
                  type="checkbox"
                  checked={selectedField.show_text !== false}
                  onChange={(e) => updateField(selectedField.key, { show_text: e.target.checked })}
                  data-testid="lprop-show-barcode-text"
                />
                Show barcode text below bars
              </label>
            )}
          </div>
        )}
        <div className="mt-6 text-[11px] text-slate-500 leading-relaxed">
          Positions are saved as percentages of the label cell, so labels stay correctly aligned if you change paper size or grid dimensions.
        </div>
      </aside>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] text-slate-500 block mb-1">{label}</label>
      <input
        type="number"
        value={value || 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="h-8 border border-slate-300 px-2 text-xs w-full"
        data-testid={testid}
      />
    </div>
  );
}

function FieldPreview({ field }) {
  const style = {
    fontSize: field.font_size,
    fontWeight: field.bold ? 700 : 400,
    textAlign: field.align,
    width: "100%",
    pointerEvents: "none",
    userSelect: "none",
    lineHeight: 1.1,
  };
  if (field.key === "barcode") {
    const showText = field.show_text !== false;
    return (
      <div style={{ ...style, fontFamily: "monospace", height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, background: "repeating-linear-gradient(90deg, #111 0 1px, transparent 1px 3px)" }} />
        {showText && (
          <div style={{ fontSize: Math.max(6, field.font_size - 1), textAlign: "center", paddingTop: 2 }}>{SAMPLE.barcode}</div>
        )}
      </div>
    );
  }
  return <div style={style}>{SAMPLE[field.key] || field.label}</div>;
}
